import { Alert, Button, Layout, Modal, Spin } from 'antd';
import React from 'react';
import Icon from '../../components/icon/Icon';
import OfflineStatusControl from '../../components/OfflineStatusControl';
import { PENALTY_5M_CENTER_X, PENALTY_5M_CENTER_Y } from '../../constants/shotPenaltyLocation';
import { ClockState } from '../../core/models/enum';
import {
    CountdownAnchor,
    readCountdown,
    splitWholeSeconds,
    startCountdown,
} from '../../helpers/countdownClock';
import {
    buildTeamYellowCardDisciplineLine,
    compareEventsByTime,
    filterPeriodEvents,
    getEventCloudLabel,
    getMostRecentCommittedEvent,
    getSuggestedPowerPlayFromBrutality240IfEligible,
    getSuggestedPowerPlayShotTypeIfEligible,
    getTeamId,
    isOpposingPenaltyExclusionForShot,
    refreshMatchGoals,
} from '../../helpers/EventHelper';
import { payloadFromCommand } from '../../helpers/eventMutations';
import { eventSubmissionQueue } from '../../helpers/eventSubmissionQueue';
import { applyRemoteDelete, MatchEventsHub } from '../../helpers/matchEventsHub';
import {
    errorWithUserMessage,
    popupConfirmWithCallback,
    success,
} from '../../helpers/NotificationHelper';
import { newClientEventKey } from '../../helpers/offlineEventStore';
import {
    adjustMatchGoalsForShot,
    applyOutboxRowsToState,
    bumpMatchForPendingGoals,
    getClientEventKey,
    isPendingEvent,
    markPending,
    mergeServerWithLocalPending,
    optimisticEventFromCommand,
    removePendingByKey,
    replacePendingWithServer,
    upsertServerEvent,
} from '../../helpers/pendingEventHelpers';
import {
    ApiException,
    CardTypeEnum,
    ChallengeRequest,
    CoachesClient,
    CoachVm,
    CreateEventCommand,
    CreateMatchPlayerCommandOfMatchPlayer,
    EventPeriodEnum,
    EventsClient,
    EventTypeEnum,
    EventVm,
    ExclusionRequest,
    ExclusionTypeEnum,
    GoalkeeperChangeRequest,
    MatchCoachVm,
    MatchDetailVm,
    MatchesClient,
    MatchStatusEnum,
    OfficialsClient,
    OfficialVm,
    PlayerPositionEnum,
    ShotRequest,
    ShotTypeEnum,
    UpdateEventCommand,
    UpdateMatchCommand,
    UpdateMatchDisciplinaryNoteCommand,
} from '../../utils/api';
import { Props } from './index';
import MatchClock from './match-clock';
import {
    formatMatchSecondsAsClock,
    OvertimeUiMode,
    shortPeriodClockLabel,
} from './match-clock/periodLabels';
import Card from './match-event-form/card';
import Challenge from './match-event-form/challenge';
import CoachExclusion from './match-event-form/coach-exclusion';
import Exclusion from './match-event-form/exclusion';
import GoalkeeperChangeForm from './match-event-form/goalkeeper-change';
import Shot from './match-event-form/shot';
import SwimOff from './match-event-form/swim-off';
import Timeout from './match-event-form/timeout';
import Turnover from './match-event-form/turnover';
import MatchEventLog from './match-event-log';
import MatchLineup from './match-lineup';

const { Content } = Layout;

const INITIAL_TIME = 8 * 60;
const CLOCK_STOPPING_EVENTS = [EventTypeEnum.Timeout, EventTypeEnum.Challenge];

export interface State {
    match?: MatchDetailVm;
    coaches: CoachVm[];
    officials: OfficialVm[];
    events: EventVm[];
    selectedHomeKeeper: number;
    selectedAwayKeeper: number;
    currentPeriod: EventPeriodEnum;
    clockState: ClockState;
    currentTime: number;
    eventPeriod: EventPeriodEnum;
    eventTime: number;
    event: EventVm | CreateEventCommand | UpdateEventCommand | null;
    isEventValid: boolean;
    isLoading: boolean;
    showGkSelectionModal: boolean;
    /** True while starting-GK events are being created (modal OK is loading) */
    isConfirmingStartingGk: boolean;
    pendingHomeGk: number | null;
    pendingAwayGk: number | null;
    /** UI only: OT play vs penalty shootout; same `periodId` for both. */
    overtimeUiMode: OvertimeUiMode;
    /** Non–Q1 swim-off: reminder modal before starting swim-off (avoids blocking GK picks). */
    showPreSwimOffGkModal: boolean;
    /** Period the swim-off belongs to while `showPreSwimOffGkModal` is open. */
    preSwimOffPeriod: EventPeriodEnum | null;
    /** After "Record goalkeeper changes first": open swim-off once GK form is saved or discarded. */
    deferredSwimOffPeriodId: EventPeriodEnum | null;
}

class MatchReport extends React.PureComponent<Props, State> {
    timer!: NodeJS.Timeout | null;

    /** Wall-clock anchor so stop/start keeps sub-second remainder (UI still shows whole seconds). */
    private clockAnchor: CountdownAnchor | null = null;

    private isMatchReportMounted = false;

    // Realtime sync connection to /hubs/match (events only).
    private eventsHub = new MatchEventsHub();

    private unsubOutbox: (() => void) | null = null;

    constructor(props: Props) {
        super(props);

        this.state = {
            coaches: [],
            officials: [],
            events: [],
            selectedAwayKeeper: 1,
            selectedHomeKeeper: 1,
            currentPeriod: EventPeriodEnum.First_Period,
            clockState: ClockState.Stopped,
            currentTime: INITIAL_TIME,
            eventPeriod: EventPeriodEnum.First_Period,
            eventTime: INITIAL_TIME,
            event: null,
            isEventValid: false,
            isLoading: false,
            showGkSelectionModal: false,
            isConfirmingStartingGk: false,
            pendingHomeGk: null,
            pendingAwayGk: null,
            overtimeUiMode: 'play',
            showPreSwimOffGkModal: false,
            preSwimOffPeriod: null,
            deferredSwimOffPeriodId: null,
        };

        this.timer = null;
    }

    /**
     * Derive active goalkeepers from starting lineup (PositionId = Goalkeeper)
     * plus any Goalkeeper_Change events in chronological order.
     * When upToPeriod/upToTime are provided, only GK changes at or before that
     * point in time are considered (time-aware highlight).
     */
    static deriveActiveGoalkeepers(
        match: MatchDetailVm,
        events: EventVm[],
        upToPeriod?: number,
        upToTime?: number
    ): { homeKeeper: number; awayKeeper: number } {
        const lineups = (match.players as CreateMatchPlayerCommandOfMatchPlayer[]) || [];

        // Start from lineup: find players with Goalkeeper position
        const homeGkPlayer = lineups.find(
            (l) => l.teamId === match.homeTeamId && l.positionId === PlayerPositionEnum.Goalkeeper
        );
        const awayGkPlayer = lineups.find(
            (l) => l.teamId === match.awayTeamId && l.positionId === PlayerPositionEnum.Goalkeeper
        );
        let homeKeeper = homeGkPlayer?.number || 1;
        let awayKeeper = awayGkPlayer?.number || 1;

        // Walk GK change events in chronological order (by Id)
        const gkChanges = events
            .filter((e) => e.typeId === EventTypeEnum.Goalkeeper_Change && e.goalkeeperChange)
            .sort((a, b) => a.id - b.id);

        for (const evt of gkChanges) {
            // If time-aware, skip GK changes that haven't happened yet at the
            // current clock position. Clock counts down (8:00 → 0:00), so a
            // lower remaining time means later in the quarter.
            if (upToPeriod !== undefined && upToTime !== undefined) {
                const evtTimeInSeconds = evt.minute * 60 + evt.seconds;
                if (evt.periodId > upToPeriod) continue;
                if (evt.periodId === upToPeriod && evtTimeInSeconds < upToTime) continue;
            }

            const newGk = lineups.find(
                (l) => l.id === evt.goalkeeperChange!.newGoalkeeperMatchPlayerId
            );
            if (!newGk) continue;
            const isHome = evt.goalkeeperChange!.teamId === match.homeTeamId;
            if (isHome) {
                homeKeeper = newGk.number;
            } else {
                awayKeeper = newGk.number;
            }
        }

        return { homeKeeper, awayKeeper };
    }

    public componentDidMount = () => {
        this.isMatchReportMounted = true;
        this.getMatch();
        this.getCoaches();
        this.getOfficials();
        this.connectEventsHub();
        eventSubmissionQueue.start();
        this.unsubOutbox = eventSubmissionQueue.subscribe((e) => {
            if (!this.isMatchReportMounted) return;
            if (e.type === 'wentOnline' || e.type === 'syncRequested') {
                // Outbox already drained (or Arena still unreachable). Refetch once.
                void this.resyncFromServer();
                return;
            }
            const matchId = this.state.match?.id;
            if (!matchId) return;
            if (e.type === 'published' && e.matchId === matchId) {
                if (e.op === 'delete') {
                    void this.handleEventDeleted(e.result);
                    return;
                }
                void this.handleEventSaved(e.result, e.op === 'update', e.clientEventKey);
            }
            if (e.type === 'cancelled' && e.matchId === matchId) {
                this.setState((prev) => ({
                    events: removePendingByKey(prev.events, e.clientEventKey),
                }));
            }
        });
    };

    public componentWillUnmount = (): void => {
        this.isMatchReportMounted = false;
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
        this.clockAnchor = null;
        void this.eventsHub.disconnect();
        this.unsubOutbox?.();
        this.unsubOutbox = null;
    };

    // --- Realtime event sync (MatchHub) ---
    // Other instances' create/update/delete arrive here; clock stays local.

    private connectEventsHub = (): void => {
        const { matchId } = this.props.match.params;
        if (!matchId) return;
        void this.eventsHub.connectAndJoin(parseInt(matchId, 10), {
            onRemoteCreate: this.handleRemoteEventUpsert,
            onRemoteUpdate: this.handleRemoteEventUpsert,
            onRemoteDelete: this.handleRemoteEventDeleted,
            onResync: this.resyncFromServer,
        });
    };

    private handleRemoteEventUpsert = (event: EventVm): void => {
        // GK changes cascade savedById updates onto other events on the
        // backend; only a full refetch reflects that (same as the local path).
        if (event.typeId === EventTypeEnum.Goalkeeper_Change) {
            void this.resyncFromServer();
            return;
        }
        this.setState((prev) => {
            if (!prev.match) return null;
            // Drop optimistic twin + upsert by id (SignalR echo after local create).
            const newEvents = upsertServerEvent(prev.events, event);
            const newMatch = { ...prev.match, ...event.match } as MatchDetailVm;
            const { homeKeeper, awayKeeper } = MatchReport.deriveActiveGoalkeepers(
                newMatch,
                newEvents
            );
            return {
                events: newEvents,
                match: newMatch,
                selectedHomeKeeper: homeKeeper,
                selectedAwayKeeper: awayKeeper,
            };
        });
    };

    private handleRemoteEventDeleted = (event: EventVm): void => {
        // Delete broadcasts carry only id + match, so identify the deleted
        // event from our local copy.
        const deletedEvent = this.state.events.find((e: EventVm): boolean => e.id === event.id);
        if (!deletedEvent) return; // we deleted it ourselves (echo) or never had it
        if (deletedEvent.typeId === EventTypeEnum.Goalkeeper_Change) {
            void this.resyncFromServer();
            return;
        }
        this.setState((prev) => {
            if (!prev.match) return null;
            const newEvents = applyRemoteDelete(prev.events, event);
            const newMatch =
                deletedEvent.typeId === EventTypeEnum.Shot && event.match
                    ? refreshMatchGoals(prev.match, event)
                    : prev.match;
            const { homeKeeper, awayKeeper } = MatchReport.deriveActiveGoalkeepers(
                newMatch,
                newEvents
            );
            return {
                events: newEvents,
                match: newMatch,
                selectedHomeKeeper: homeKeeper,
                selectedAwayKeeper: awayKeeper,
            };
        });
    };

    /** Full events + match refetch (initial join and every reconnect). Clock untouched. */
    private resyncFromServer = async (): Promise<void> => {
        const { matchId } = this.props.match.params;
        if (!matchId || !this.isMatchReportMounted) return;
        const id = parseInt(matchId, 10);
        const [match, events] = await Promise.all([
            new MatchesClient().getById(id),
            new EventsClient().getByMatchId(id),
        ]);
        if (!this.isMatchReportMounted) return;
        const { homeKeeper, awayKeeper } = MatchReport.deriveActiveGoalkeepers(match, events);
        // Keep any still-pending local rows; drop temp twins already on the server.
        this.setState((prev) => {
            const merged = mergeServerWithLocalPending(events, prev.events);
            return {
                match: bumpMatchForPendingGoals(match, merged),
                events: merged,
                selectedHomeKeeper: homeKeeper,
                selectedAwayKeeper: awayKeeper,
            };
        });
        void this.rehydratePendingOutbox();
    };

    private rehydratePendingOutbox = async (): Promise<void> => {
        const match = this.state.match;
        if (!match || !this.isMatchReportMounted) return;
        const rows = await eventSubmissionQueue.listByMatch(match.id);
        if (!this.isMatchReportMounted || rows.length === 0) return;
        this.setState((prev) => {
            if (!prev.match) return null;
            const next = applyOutboxRowsToState(rows, prev.events, prev.match);
            const { homeKeeper, awayKeeper } = MatchReport.deriveActiveGoalkeepers(
                next.match,
                next.events
            );
            return {
                events: next.events,
                match: next.match,
                selectedHomeKeeper: homeKeeper,
                selectedAwayKeeper: awayKeeper,
            };
        });
    };

    private hasFailedChallengeForTeam(teamId: number | undefined): boolean {
        if (teamId == null) {
            return false;
        }
        return this.state.events.some(
            (e: EventVm): boolean =>
                e.typeId === EventTypeEnum.Challenge &&
                e.challenge?.teamId === teamId &&
                e.challenge.isSuccessful === false
        );
    }

    private getMatch = async () => {
        const {
            match: {
                params: { matchId },
            },
        } = this.props;

        if (!matchId) return;

        this.setState({
            isLoading: true,
        });

        const matchClient = new MatchesClient();
        const eventClient = new EventsClient();

        const match = await matchClient.getById(parseInt(matchId, 10));
        const events = await eventClient.getByMatchId(parseInt(matchId, 10));

        if (!this.isMatchReportMounted) {
            return;
        }

        const { homeKeeper, awayKeeper } = MatchReport.deriveActiveGoalkeepers(match, events);
        const hasQ1Swim = events.some(
            (e) =>
                e.typeId === EventTypeEnum.Swim_Off && e.periodId === EventPeriodEnum.First_Period
        );

        this.setState(
            {
                match,
                events,
                selectedHomeKeeper: homeKeeper,
                selectedAwayKeeper: awayKeeper,
            },
            () => {
                if (!this.isMatchReportMounted) {
                    return;
                }
                if (!hasQ1Swim) {
                    this.initSwimOff(events.length === 0, EventPeriodEnum.First_Period);
                } else {
                    this.initClock();
                }
                void this.rehydratePendingOutbox();
            }
        );

        if (!this.isMatchReportMounted) {
            return;
        }

        this.setState({
            isLoading: false,
        });
    };

    private createStartingGoalkeeperEvents = async (
        homeCap: number,
        awayCap: number
    ): Promise<boolean> => {
        const { match, eventTime, eventPeriod } = this.state;
        if (!match) return false;

        const lineups = (match.players as CreateMatchPlayerCommandOfMatchPlayer[]) || [];
        const homePlayer = lineups.find(
            (l) => l.teamId === match.homeTeamId && l.number === homeCap
        );
        const awayPlayer = lineups.find(
            (l) => l.teamId === match.awayTeamId && l.number === awayCap
        );
        if (!homePlayer || !awayPlayer) {
            return false;
        }

        const { minute, seconds: sec } = splitWholeSeconds(eventTime);
        const homeGkId = homePlayer.id;
        const awayGkId = awayPlayer.id;

        const client = new EventsClient();
        const buildCmd = (teamId: number, gk: CreateMatchPlayerCommandOfMatchPlayer) =>
            new CreateEventCommand({
                matchId: match.id,
                typeId: EventTypeEnum.Goalkeeper_Change,
                periodId: eventPeriod,
                minute,
                seconds: sec,
                goalkeeperChange: new GoalkeeperChangeRequest({
                    id: 0,
                    teamId,
                    newGoalkeeperMatchPlayerId: gk.id,
                    previousGoalkeeperMatchPlayerId: undefined,
                }),
                activeHomeGoalkeeperMatchPlayerId: homeGkId,
                activeAwayGoalkeeperMatchPlayerId: awayGkId,
            } as any);

        try {
            await client.create(buildCmd(match.homeTeamId, homePlayer));
            await client.create(buildCmd(match.awayTeamId, awayPlayer));
        } catch (error) {
            if (error instanceof ApiException) {
                errorWithUserMessage(error.response);
            } else {
                errorWithUserMessage('Error saving starting goalkeepers.');
            }
            return false;
        }

        const eventClient = new EventsClient();
        const matchClient = new MatchesClient();
        const [freshEvents, freshMatch] = await Promise.all([
            eventClient.getByMatchId(match.id),
            matchClient.getById(match.id),
        ]);
        if (!this.isMatchReportMounted) {
            return false;
        }
        const { homeKeeper, awayKeeper } = MatchReport.deriveActiveGoalkeepers(
            freshMatch,
            freshEvents
        );
        this.setState({
            match: freshMatch,
            events: freshEvents,
            selectedHomeKeeper: homeKeeper,
            selectedAwayKeeper: awayKeeper,
        });
        return true;
    };

    private getCoaches = async () => {
        const coachClient = new CoachesClient();
        const coaches = await coachClient.getAll();

        if (!this.isMatchReportMounted) {
            return;
        }

        this.setState({
            coaches,
        });
    };

    private getOfficials = async () => {
        const officialClient = new OfficialsClient();
        const officials = await officialClient.getAll();

        if (!this.isMatchReportMounted) {
            return;
        }

        this.setState({
            officials,
        });
    };

    private showActiveGoalkeepersInfoModal = (): void => {
        const { match, selectedHomeKeeper, selectedAwayKeeper } = this.state;

        if (!match) return;

        const lineups = (match.players as CreateMatchPlayerCommandOfMatchPlayer[]) || [];
        const homeGkLineup = lineups.find(
            (l) => l.teamId === match.homeTeamId && l.number === selectedHomeKeeper
        );
        const awayGkLineup = lineups.find(
            (l) => l.teamId === match.awayTeamId && l.number === selectedAwayKeeper
        );
        const homeCapDisplay = homeGkLineup
            ? homeGkLineup.capNumber || homeGkLineup.number
            : selectedHomeKeeper;
        const awayCapDisplay = awayGkLineup
            ? awayGkLineup.capNumber || awayGkLineup.number
            : selectedAwayKeeper;
        Modal.info({
            title: 'Active goalkeepers',
            content: `White #${homeCapDisplay} and blue #${awayCapDisplay} are active goalkeepers. Double-click a cap number to change.`,
            okText: 'OK',
        });
    };

    private initSwimOff = (isFirstQuarter: boolean = false, swimOffPeriod?: EventPeriodEnum) => {
        const { match, eventPeriod } = this.state;

        if (!match) return;

        const targetPeriod = swimOffPeriod ?? eventPeriod;

        if (isFirstQuarter) {
            this.setState({
                showGkSelectionModal: true,
                isConfirmingStartingGk: false,
                pendingHomeGk: null,
                pendingAwayGk: null,
            });
        } else {
            this.setState({
                showPreSwimOffGkModal: true,
                preSwimOffPeriod: targetPeriod,
            });
        }
    };

    private maybeStartDeferredSwimOff = () => {
        const { deferredSwimOffPeriodId, events, match } = this.state;

        if (deferredSwimOffPeriodId === null || !match) {
            return;
        }

        const alreadyHasSwimOff = events.some(
            (e) => e.typeId === EventTypeEnum.Swim_Off && e.periodId === deferredSwimOffPeriodId
        );

        if (alreadyHasSwimOff) {
            this.setState({ deferredSwimOffPeriodId: null });
            return;
        }

        const targetPeriod = deferredSwimOffPeriodId;
        this.setState({ deferredSwimOffPeriodId: null }, () => {
            this.initEvent(EventTypeEnum.Swim_Off, undefined, targetPeriod);
        });
    };

    private initClock = () => {
        const { events } = this.state;

        const sortedEvents = events.sort(compareEventsByTime);
        let newestEvent = {} as EventVm;

        const q1Events = filterPeriodEvents(sortedEvents, EventPeriodEnum.First_Period);
        const q2Events = filterPeriodEvents(sortedEvents, EventPeriodEnum.Second_Period);
        const q3Events = filterPeriodEvents(sortedEvents, EventPeriodEnum.Third_Period);
        const q4Events = filterPeriodEvents(sortedEvents, EventPeriodEnum.Fourth_Period);
        const psoEvents = filterPeriodEvents(sortedEvents, EventPeriodEnum.Overtime);

        if (psoEvents.length > 0) {
            [newestEvent] = psoEvents;
        } else if (q4Events.length > 0) {
            [newestEvent] = q4Events;
        } else if (q3Events.length > 0) {
            [newestEvent] = q3Events;
        } else if (q2Events.length > 0) {
            [newestEvent] = q2Events;
        } else if (q1Events.length > 0) {
            [newestEvent] = q1Events;
        }

        if (!this.timer || (this.timer && this.isClockStoppingEvent(newestEvent))) {
            this.setState({
                currentPeriod: newestEvent.periodId,
                currentTime: newestEvent.minute * 60 + newestEvent.seconds,
                eventPeriod: newestEvent.periodId,
                eventTime: newestEvent.minute * 60 + newestEvent.seconds,
                overtimeUiMode: 'play',
            });
        }
    };

    private isClockStoppingEvent = (event: EventVm): boolean => {
        return (
            !!event.timeout ||
            !!event.challenge ||
            event.shot?.isGoal ||
            event.exclusion?.typeId === ExclusionTypeEnum.Brutality ||
            event.exclusion?.typeId === ExclusionTypeEnum.Penalty
        );
    };

    private startClock = () => {
        const { clockState, currentTime, eventTime } = this.state;
        if (clockState === ClockState.Running && this.timer) return;

        // Resume from wall-clock if we were paused with the interval still alive;
        // otherwise take the frozen value that will be shown after start.
        const remaining =
            clockState === ClockState.Stopped
                ? eventTime
                : this.clockAnchor
                ? readCountdown(this.clockAnchor)
                : currentTime;

        this.clockAnchor = startCountdown(remaining);
        if (!this.timer) {
            // 100ms so floored MM:SS flips on the real second boundary, not the interval phase.
            this.timer = setInterval(() => this.tickClock(), 100);
        }

        this.setState(
            (prevState: State): State => ({
                ...prevState,
                clockState: ClockState.Running,
                currentTime: remaining,
                eventPeriod:
                    prevState.clockState === ClockState.Paused
                        ? prevState.currentPeriod
                        : prevState.eventPeriod,
                currentPeriod:
                    prevState.clockState === ClockState.Stopped
                        ? prevState.eventPeriod
                        : prevState.currentPeriod,
                eventTime:
                    prevState.clockState === ClockState.Paused
                        ? remaining
                        : prevState.clockState === ClockState.Stopped
                        ? prevState.eventTime
                        : remaining,
            })
        );
    };

    /** Stop and freeze fractional remaining. Returns the frozen value for event capture. */
    private stopClock = (): number => {
        const { clockState, eventTime, currentTime } = this.state;
        const wall = this.clockAnchor ? readCountdown(this.clockAnchor) : currentTime;
        // From a paused event form (e.g. exclusion → Penalty), keep the form
        // time - do not jump the game clock to wherever the wall advanced.
        const frozen = clockState === ClockState.Paused ? eventTime : wall;
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
        this.clockAnchor = null;
        this.setState(
            (prevState: State): State => ({
                ...prevState,
                clockState: ClockState.Stopped,
                currentTime: frozen,
                eventTime: frozen,
            })
        );
        return frozen;
    };

    private pauseClock = () => {
        const { clockState } = this.state;

        if (clockState === ClockState.Running) {
            const frozen = this.clockAnchor
                ? readCountdown(this.clockAnchor)
                : this.state.currentTime;
            this.setState(
                (prevState: State): State => ({
                    ...prevState,
                    clockState: ClockState.Paused,
                    currentTime: frozen,
                    eventTime: frozen,
                })
            );
        }
    };

    private resetClock = () => {
        this.stopClock();

        this.setState({
            currentTime: INITIAL_TIME,
            eventTime: INITIAL_TIME,
        });
    };

    private setPeriod = (period: EventPeriodEnum, overtimeMode: OvertimeUiMode = 'play') => {
        const { events } = this.state;
        const nextOvertimeUiMode: OvertimeUiMode =
            period === EventPeriodEnum.Overtime ? overtimeMode : 'play';

        const isOvertimePenaltyShootout =
            period === EventPeriodEnum.Overtime && overtimeMode === 'penalty_shootout';

        if (isOvertimePenaltyShootout) {
            const enteringPso =
                this.state.eventPeriod !== EventPeriodEnum.Overtime ||
                this.state.overtimeUiMode !== 'penalty_shootout';
            if (enteringPso) {
                this.showActiveGoalkeepersInfoModal();
            }
        } else if (
            events.filter(
                (x: EventVm): boolean =>
                    x.typeId === EventTypeEnum.Swim_Off && x.periodId === period
            ).length === 0
        ) {
            this.initSwimOff(false, period);
        }

        this.setState((prevState: State): State => {
            const isPso =
                period === EventPeriodEnum.Overtime && overtimeMode === 'penalty_shootout';

            const leavingPreSwimPeriod =
                prevState.preSwimOffPeriod != null && period !== prevState.preSwimOffPeriod;

            if (isPso) {
                if (this.timer) {
                    clearInterval(this.timer);
                    this.timer = null;
                }
                this.clockAnchor = null;
            } else if (prevState.clockState !== ClockState.Paused) {
                this.resetClock();
            }

            const mergeEventTime = isPso ? 0 : prevState.eventTime;

            let nextEvent: State['event'] = prevState.event;
            if (
                prevState.event?.typeId === EventTypeEnum.Shot &&
                prevState.event.shot &&
                prevState.match
            ) {
                const ev = prevState.event as EventVm;
                const nextShot = this.mergeShotWithAutomaticPresets(
                    prevState.event.shot,
                    prevState.events,
                    prevState.event.shot.teamId,
                    prevState.match,
                    period,
                    mergeEventTime,
                    ev.shotId ?? 0,
                    nextOvertimeUiMode
                );
                nextEvent = {
                    ...prevState.event,
                    shot: nextShot,
                } as UpdateEventCommand;
            }

            const nextTimes: Partial<Pick<State, 'eventTime' | 'currentTime' | 'clockState'>> =
                isPso
                    ? {
                          eventTime: 0,
                          currentTime: 0,
                          clockState:
                              prevState.clockState === ClockState.Running
                                  ? ClockState.Stopped
                                  : prevState.clockState,
                      }
                    : {};

            return {
                ...prevState,
                ...nextTimes,
                event: nextEvent,
                overtimeUiMode: nextOvertimeUiMode,
                currentPeriod:
                    prevState.clockState === ClockState.Paused ? prevState.currentPeriod : period,
                eventPeriod: period,
                showPreSwimOffGkModal: leavingPreSwimPeriod
                    ? false
                    : prevState.showPreSwimOffGkModal,
                preSwimOffPeriod: leavingPreSwimPeriod ? null : prevState.preSwimOffPeriod,
                deferredSwimOffPeriodId:
                    prevState.deferredSwimOffPeriodId != null &&
                    period !== prevState.deferredSwimOffPeriodId
                        ? null
                        : prevState.deferredSwimOffPeriodId,
            };
        });
    };

    private modifyClock = (amount: number) => {
        const { clockState } = this.state;
        if (clockState === ClockState.Running) return;

        this.setState((prevState: State): State => {
            const newTime = prevState.eventTime + amount;
            const nextEventTime = newTime < 0 ? prevState.eventTime : newTime;

            let nextEvent: State['event'] = prevState.event;
            if (
                prevState.event?.typeId === EventTypeEnum.Shot &&
                prevState.event.shot &&
                prevState.match
            ) {
                const ev = prevState.event as EventVm;
                const nextShot = this.mergeShotWithAutomaticPresets(
                    prevState.event.shot,
                    prevState.events,
                    prevState.event.shot.teamId,
                    prevState.match,
                    prevState.eventPeriod,
                    nextEventTime,
                    ev.shotId ?? 0,
                    prevState.overtimeUiMode
                );
                nextEvent = {
                    ...prevState.event,
                    shot: nextShot,
                } as UpdateEventCommand;
            }

            return {
                ...prevState,
                event: nextEvent,
                eventTime: nextEventTime,
                currentTime:
                    prevState.clockState === ClockState.Paused || newTime < 0
                        ? prevState.currentTime
                        : nextEventTime,
            };
        });
    };

    private tickClock = () => {
        if (!this.clockAnchor) return;
        const remaining = readCountdown(this.clockAnchor);
        if (remaining <= 0) {
            this.clockAnchor = null;
            if (this.timer) {
                clearInterval(this.timer);
                this.timer = null;
            }
            this.setState(
                (prevState: State): State => ({
                    ...prevState,
                    clockState: ClockState.Stopped,
                    currentTime: 0,
                    eventTime: prevState.clockState === ClockState.Paused ? prevState.eventTime : 0,
                })
            );
            return;
        }

        this.setState(
            (prevState: State): State => ({
                ...prevState,
                currentTime: remaining,
                eventTime:
                    prevState.clockState === ClockState.Paused ? prevState.eventTime : remaining,
            })
        );
    };

    private mergeShotWithAutomaticPresets = (
        shot: ShotRequest,
        events: EventVm[],
        teamId: number,
        match: MatchDetailVm,
        eventPeriod: EventPeriodEnum,
        eventTime: number,
        shotRowId: number,
        overtimeUiMode: OvertimeUiMode = 'play'
    ): ShotRequest => {
        if (eventPeriod === EventPeriodEnum.Overtime && overtimeUiMode === 'penalty_shootout') {
            return new ShotRequest({
                ...shot,
                id: shotRowId,
                typeId: ShotTypeEnum.Penalty_Shootout,
                fakeCount: 0,
                isFastBreak: false,
                isDirectFromFoul: false,
                locationX: PENALTY_5M_CENTER_X,
                locationY: PENALTY_5M_CENTER_Y,
            });
        }
        const { minute, seconds: shotSecs } = splitWholeSeconds(eventTime);
        const lastSaved = getMostRecentCommittedEvent(events);
        if (lastSaved && isOpposingPenaltyExclusionForShot(lastSaved, teamId)) {
            return new ShotRequest({
                ...shot,
                id: shotRowId,
                typeId: ShotTypeEnum.Penalty,
                fakeCount: 0,
                isFastBreak: false,
                isDirectFromFoul: false,
                locationX: PENALTY_5M_CENTER_X,
                locationY: PENALTY_5M_CENTER_Y,
            });
        }
        const brutalityPp = getSuggestedPowerPlayFromBrutality240IfEligible(
            events,
            teamId,
            eventPeriod,
            minute,
            shotSecs
        );
        if (brutalityPp !== undefined) {
            return new ShotRequest({
                ...shot,
                id: shotRowId,
                typeId: brutalityPp,
            });
        }
        const shortWindowPp = getSuggestedPowerPlayShotTypeIfEligible(
            events,
            teamId,
            teamId === match.homeTeamId ? match.awayTeamId : match.homeTeamId,
            eventPeriod,
            minute,
            shotSecs
        );
        if (shortWindowPp !== undefined) {
            return new ShotRequest({
                ...shot,
                id: shotRowId,
                typeId: shortWindowPp,
            });
        }
        if (shot.typeId === ShotTypeEnum.Power_Play) {
            return new ShotRequest({
                ...shot,
                id: shotRowId,
                typeId: ShotTypeEnum.Regular_Attack,
            });
        }
        return new ShotRequest({ ...shot, id: shotRowId });
    };

    private initEvent = (
        type: EventTypeEnum,
        eventTypeValues?: any,
        periodIdForNewEvent?: EventPeriodEnum
    ) => {
        const { clockState, match, eventPeriod, eventTime, overtimeUiMode } = this.state;

        if (!match) return;

        const effectiveEventPeriod = periodIdForNewEvent ?? eventPeriod;

        const shouldStopClockOnInit =
            CLOCK_STOPPING_EVENTS.includes(type) ||
            (type === EventTypeEnum.Shot && !!eventTypeValues?.isGoal);

        // Freeze wall-clock so event minute/seconds match the tap instant.
        let captureTime = eventTime;
        if (shouldStopClockOnInit) {
            captureTime = this.stopClock();
        } else if (clockState === ClockState.Running) {
            this.pauseClock();
            captureTime = this.clockAnchor ? readCountdown(this.clockAnchor) : this.state.eventTime;
        }

        const { minute, seconds } = splitWholeSeconds(captureTime);

        const { homeGkId, awayGkId } = this.getActiveGoalkeeperMatchPlayerIds(
            effectiveEventPeriod,
            captureTime
        );

        let initialShot: ShotRequest | undefined =
            type === EventTypeEnum.Shot && eventTypeValues
                ? new ShotRequest({
                      id: 0,
                      teamId: eventTypeValues.teamId,
                      typeId: eventTypeValues.typeId ?? ShotTypeEnum.Regular_Attack,
                      takenById: eventTypeValues.takenById,
                      blockedById: eventTypeValues.blockedById,
                      assistedById: eventTypeValues.assistedById,
                      savedById: eventTypeValues.savedById,
                      isGoal: eventTypeValues.isGoal ?? false,
                      isFastBreak: eventTypeValues.isFastBreak ?? false,
                      isDirectFromFoul: eventTypeValues.isDirectFromFoul ?? false,
                      shotTechnique: eventTypeValues.shotTechnique,
                      fakeCount: eventTypeValues.fakeCount ?? 0,
                      locationX: eventTypeValues.locationX,
                      locationY: eventTypeValues.locationY,
                      targetX: eventTypeValues.targetX,
                      targetY: eventTypeValues.targetY,
                  })
                : undefined;

        if (initialShot && eventTypeValues?.teamId != null) {
            initialShot = this.mergeShotWithAutomaticPresets(
                initialShot,
                this.state.events,
                eventTypeValues.teamId,
                match,
                effectiveEventPeriod,
                captureTime,
                0,
                overtimeUiMode
            );
        }

        const initialExclusion: ExclusionRequest | undefined =
            type === EventTypeEnum.Exclusion && eventTypeValues
                ? new ExclusionRequest({
                      id: 0,
                      teamId: eventTypeValues.teamId,
                      typeId: eventTypeValues.typeId ?? ExclusionTypeEnum.Exclusion,
                      excludedPlayerId: eventTypeValues.excludedPlayerId,
                      excludedCoachId: eventTypeValues.excludedCoachId,
                      fouledPlayerId: eventTypeValues.fouledPlayerId,
                      assistedById: eventTypeValues.assistedById,
                      isPenaltyExclusion: eventTypeValues.isPenaltyExclusion ?? false,
                      isDoubleExclusion: eventTypeValues.isDoubleExclusion ?? false,
                      locationX: eventTypeValues.locationX,
                      locationY: eventTypeValues.locationY,
                  })
                : undefined;

        const initialChallenge: ChallengeRequest | undefined =
            type === EventTypeEnum.Challenge && eventTypeValues
                ? new ChallengeRequest({
                      id: 0,
                      teamId: eventTypeValues.teamId,
                      isSuccessful: undefined,
                  } as any)
                : undefined;

        const newEvent: CreateEventCommand = new CreateEventCommand({
            matchId: match.id,
            typeId: type,
            periodId: effectiveEventPeriod,
            minute,
            seconds,
            turnover: type === EventTypeEnum.Turnover ? eventTypeValues : undefined,
            shot: initialShot,
            timeout: type === EventTypeEnum.Timeout ? eventTypeValues : undefined,
            challenge: initialChallenge,
            swimoff: type === EventTypeEnum.Swim_Off ? eventTypeValues : undefined,
            card: type === EventTypeEnum.Card ? eventTypeValues : undefined,
            exclusion: initialExclusion,
            goalkeeperChange:
                type === EventTypeEnum.Goalkeeper_Change ? eventTypeValues : undefined,
            activeHomeGoalkeeperMatchPlayerId: homeGkId,
            activeAwayGoalkeeperMatchPlayerId: awayGkId,
        } as any);

        this.setEventValidity(newEvent);

        this.setState({
            event: newEvent,
        });
    };

    public handleKeeperSelect = (cap: number, teamId: number) => {
        const { match, selectedHomeKeeper, selectedAwayKeeper, event } = this.state;
        if (!match) return;

        // Block GK change while editing another event — also when the open form is Goalkeeper_Change (cannot open a second GK change from the lineup)
        if (event) {
            Modal.warning({
                title: 'Event in progress',
                content:
                    'Save or discard the current event first. You cannot pick another cap for a new goalkeeper change while this form is open.',
            });
            return;
        }

        const isHome = teamId === match.homeTeamId;
        const currentKeeper = isHome ? selectedHomeKeeper : selectedAwayKeeper;

        // Already the active goalkeeper
        if (cap === currentKeeper) return;

        const lineups = (match.players as CreateMatchPlayerCommandOfMatchPlayer[]) || [];
        const newGkLineup = lineups.find((l) => l.teamId === teamId && l.number === cap);
        const prevGkLineup = lineups.find((l) => l.teamId === teamId && l.number === currentKeeper);

        if (!newGkLineup) return;

        const gkChangeRequest = new GoalkeeperChangeRequest({
            id: 0,
            teamId,
            newGoalkeeperMatchPlayerId: newGkLineup.id,
            previousGoalkeeperMatchPlayerId: prevGkLineup?.id,
        });

        this.initEvent(EventTypeEnum.Goalkeeper_Change, gkChangeRequest);
    };

    private setEventValidity = (event: CreateEventCommand) => {
        let isEventValid = false;
        switch (event.typeId) {
            case EventTypeEnum.Turnover:
                isEventValid = !!event.turnover;
                break;
            case EventTypeEnum.Timeout:
                isEventValid = !!event.timeout;
                break;
            case EventTypeEnum.Challenge:
                isEventValid =
                    !!event.challenge &&
                    (event.challenge.isSuccessful === true ||
                        event.challenge.isSuccessful === false);
                break;
            case EventTypeEnum.Card: {
                isEventValid = !!event.card;
                break;
            }
            case EventTypeEnum.Swim_Off: {
                isEventValid = !!event.swimoff;
                break;
            }
            case EventTypeEnum.Exclusion: {
                isEventValid = !!event.exclusion;
                break;
            }
            case EventTypeEnum.Shot: {
                isEventValid = !!event.shot;
                break;
            }
            case EventTypeEnum.Goalkeeper_Change: {
                isEventValid = !!event.goalkeeperChange;
                break;
            }
            default:
                break;
        }

        this.setState({
            isEventValid,
        });
    };

    private clearEvent = () => {
        this.setState((prevState: State): State => {
            const cleared = {
                ...prevState,
                eventPeriod: prevState.currentPeriod,
                event: null,
                isEventValid: false,
            };
            if (prevState.clockState === ClockState.Paused) {
                // Non-stopping event: resume the game clock; drop form-time edits.
                return {
                    ...cleared,
                    clockState: ClockState.Running,
                    eventTime: prevState.currentTime,
                };
            }
            if (prevState.clockState === ClockState.Stopped) {
                // Stop-clock events (timeout, challenge, goal, penalty,
                // brutality): game clock adopts the time shown/edited on the
                // form, not the original press instant.
                return {
                    ...cleared,
                    currentTime: prevState.eventTime,
                };
            }
            return {
                ...cleared,
                eventTime: prevState.currentTime,
            };
        });
    };

    public editEvent = (event: UpdateEventCommand) => {
        this.pauseClock();

        this.setEventValidity(event);

        this.setState({
            event,
            eventPeriod: event.periodId,
            eventTime: event.minute * 60 + event.seconds,
        });
    };

    private updateEvent = (event: UpdateEventCommand) => {
        const { clockState } = this.state;

        this.setEventValidity(event);

        let shouldStopClock = false;

        if (clockState !== ClockState.Stopped) {
            if (event.typeId === EventTypeEnum.Shot) {
                shouldStopClock = event.shot?.isGoal as boolean;
            }

            if (event.typeId === EventTypeEnum.Exclusion) {
                shouldStopClock =
                    event.exclusion?.typeId === ExclusionTypeEnum.Brutality ||
                    event.exclusion?.typeId === ExclusionTypeEnum.Penalty;
            }
        }

        if (shouldStopClock) {
            this.stopClock();
        }

        this.setState({
            event,
        });
    };

    private saveEvent = () => {
        const { event, isEventValid, match, eventTime, eventPeriod } = this.state;

        if (!match || !isEventValid || !event) return;

        const mappedEvent = new EventVm({ ...(event as EventVm) });
        const { minute, seconds } = splitWholeSeconds(eventTime);
        const newEvent: UpdateEventCommand = new UpdateEventCommand({
            ...(event as UpdateEventCommand),
            periodId: eventPeriod,
            minute,
            seconds,
            id: mappedEvent.id,
        });

        // Persist in the background through the shared queue (FIFO preserves the
        // double-exclusion order). The form closes and the clock resumes
        // immediately; the event lands in the log once the server responds.
        if (newEvent.exclusion?.isDoubleExclusion) {
            const fouledPlayer = newEvent.exclusion?.fouledPlayerId;
            const excludedPlayer = newEvent.exclusion?.excludedPlayerId;
            const teamId = newEvent.exclusion?.teamId;
            const newTeamId = teamId === match.homeTeamId ? match.awayTeamId : match.homeTeamId;

            const replacedEvent: CreateEventCommand = new CreateEventCommand({
                ...newEvent,
                exclusion: new ExclusionRequest({
                    ...(event.exclusion as ExclusionRequest),
                    fouledPlayerId: excludedPlayer,
                    excludedPlayerId: fouledPlayer,
                    teamId: newTeamId,
                }),
            });

            this.submitEvent(replacedEvent);
        }

        this.submitEvent(newEvent, mappedEvent.id);

        this.clearEvent();

        if (event.typeId === EventTypeEnum.Swim_Off) {
            this.startClock();
        }
    };

    private getActiveGoalkeeperMatchPlayerIds = (
        forPeriod?: number,
        forTime?: number
    ): { homeGkId?: number; awayGkId?: number } => {
        const { match, events } = this.state;
        if (!match) return {};
        const lineups = (match.players as CreateMatchPlayerCommandOfMatchPlayer[]) || [];
        const { homeKeeper, awayKeeper } = MatchReport.deriveActiveGoalkeepers(
            match,
            events,
            forPeriod,
            forTime
        );
        const homeGk = lineups.find(
            (l) => l.teamId === match.homeTeamId && l.number === homeKeeper
        );
        const awayGk = lineups.find(
            (l) => l.teamId === match.awayTeamId && l.number === awayKeeper
        );
        return { homeGkId: homeGk?.id, awayGkId: awayGk?.id };
    };

    private submitEvent = (event: EventVm | CreateEventCommand, eventId?: number): void => {
        const eventTimeInSeconds = event.minute * 60 + event.seconds;
        const { homeGkId, awayGkId } = this.getActiveGoalkeeperMatchPlayerIds(
            event.periodId,
            eventTimeInSeconds
        );
        // Capture GK at enqueue time (optimistic-as-real for offline FIFO).
        const payload = {
            matchId: event.matchId,
            typeId: event.typeId,
            periodId: event.periodId,
            minute: event.minute,
            seconds: event.seconds,
            turnover: event.turnover,
            shot: event.shot,
            timeout: event.timeout,
            challenge: event.challenge,
            swimoff: event.swimoff,
            card: event.card,
            exclusion: event.exclusion,
            goalkeeperChange: event.goalkeeperChange,
            activeHomeGoalkeeperMatchPlayerId: event.activeHomeGoalkeeperMatchPlayerId || homeGkId,
            activeAwayGoalkeeperMatchPlayerId: event.activeAwayGoalkeeperMatchPlayerId || awayGkId,
            refereeMistake: event.refereeMistake,
        };
        const isUpdate = !!eventId && eventId > 0;
        const match = this.state.match;
        if (!match) return;

        // Edit of a still-queued create: rewrite outbox row, no HTTP yet.
        if (eventId && eventId < 0) {
            const existing = this.state.events.find((e) => e.id === eventId);
            const key = existing ? getClientEventKey(existing) : undefined;
            if (key) {
                const command = CreateEventCommand.fromJS(payload);
                const optimistic = optimisticEventFromCommand(command, key);
                const label = getEventCloudLabel(event, match);
                void eventSubmissionQueue.updatePendingCreate(
                    key,
                    payloadFromCommand(command),
                    optimistic,
                    label
                );
                this.setState((prev) => {
                    const events = prev.events.map((e) => (e.id === eventId ? optimistic : e));
                    const { homeKeeper, awayKeeper } = MatchReport.deriveActiveGoalkeepers(
                        prev.match || match,
                        events
                    );
                    return {
                        events,
                        selectedHomeKeeper: homeKeeper,
                        selectedAwayKeeper: awayKeeper,
                    };
                });
                return;
            }
        }

        const clientEventKey = newClientEventKey();
        const label = getEventCloudLabel(event, match);

        if (!isUpdate) {
            const command = CreateEventCommand.fromJS(payload);
            const optimistic = optimisticEventFromCommand(command, clientEventKey);
            this.setState((prev) => {
                if (!prev.match) return null;
                let nextMatch = prev.match;
                if (command.typeId === EventTypeEnum.Shot && command.shot?.isGoal) {
                    nextMatch = adjustMatchGoalsForShot(
                        nextMatch,
                        command.shot.teamId,
                        command.periodId,
                        1
                    );
                }
                const events = [...prev.events, optimistic];
                const { homeKeeper, awayKeeper } = MatchReport.deriveActiveGoalkeepers(
                    nextMatch,
                    events
                );
                return {
                    events,
                    match: nextMatch,
                    selectedHomeKeeper: homeKeeper,
                    selectedAwayKeeper: awayKeeper,
                };
            });
            void eventSubmissionQueue.enqueue({
                matchId: match.id,
                op: 'create',
                label,
                clientEventKey,
                payload: payloadFromCommand(command),
                optimisticEvent: optimistic,
                onCancel: () => {
                    this.setState((prev) => {
                        if (!prev.match) return null;
                        let nextMatch = prev.match;
                        if (command.typeId === EventTypeEnum.Shot && command.shot?.isGoal) {
                            nextMatch = adjustMatchGoalsForShot(
                                nextMatch,
                                command.shot.teamId,
                                command.periodId,
                                -1
                            );
                        }
                        const events = removePendingByKey(prev.events, clientEventKey);
                        const { homeKeeper, awayKeeper } = MatchReport.deriveActiveGoalkeepers(
                            nextMatch,
                            events
                        );
                        return {
                            events,
                            match: nextMatch,
                            selectedHomeKeeper: homeKeeper,
                            selectedAwayKeeper: awayKeeper,
                        };
                    });
                },
            });
            return;
        }

        // Update of a published event. Keep the pre-edit row so Cancel can
        // restore it (not just unmark the edited values as if they saved).
        const originalEvent = this.state.events.find((e) => e.id === eventId);
        this.setState((prev) => ({
            events: prev.events.map((e) =>
                e.id === eventId ? markPending({ ...e, ...payload } as EventVm, clientEventKey) : e
            ),
        }));
        void eventSubmissionQueue.enqueue({
            matchId: match.id,
            op: 'update',
            label,
            clientEventKey,
            serverEventId: eventId,
            payload: payloadFromCommand({ ...payload, id: eventId }),
            onCancel: () => {
                this.setState((prev) => ({
                    events: prev.events.map((e) => {
                        if (getClientEventKey(e) !== clientEventKey) return e;
                        if (originalEvent) return originalEvent;
                        const cleared = { ...e } as EventVm;
                        delete (cleared as any).clientEventKey;
                        delete (cleared as any).isPending;
                        return cleared;
                    }),
                }));
            },
        });
    };

    private maybeAppendDisciplinaryTeamYellow = async (
        result: EventVm,
        isUpdate: boolean
    ): Promise<void> => {
        if (isUpdate) {
            return;
        }
        if (result.typeId !== EventTypeEnum.Card || result.card?.typeId !== CardTypeEnum.Yellow) {
            return;
        }
        if (result.card?.cardedCoachId || result.card?.cardedPlayerId) {
            return;
        }
        const match = this.state.match;
        if (!match) {
            return;
        }

        try {
            const line = buildTeamYellowCardDisciplineLine(result, match);
            const prev = (match.disciplinaryNote ?? '').trim();
            const next = prev ? `${prev}\n${line}` : line;

            const matchClient = new MatchesClient();
            await matchClient.updateDisciplinaryNote(
                match.id,
                new UpdateMatchDisciplinaryNoteCommand({
                    matchId: match.id,
                    disciplinaryNote: next,
                })
            );

            this.setState((prev) =>
                prev.match
                    ? {
                          match: { ...prev.match, disciplinaryNote: next } as MatchDetailVm,
                      }
                    : null
            );
        } catch {
            errorWithUserMessage('Error updating disciplinary note.');
        }
    };

    private handleRefereeMistakeChange = async (
        eventId: number,
        value: number | null
    ): Promise<void> => {
        const { events } = this.state;
        const existingEvent = events.find((e) => e.id === eventId);
        if (!existingEvent) return;

        try {
            const client = new EventsClient();
            const payload = new UpdateEventCommand({
                id: eventId,
                matchId: existingEvent.matchId,
                typeId: existingEvent.typeId,
                periodId: existingEvent.periodId,
                minute: existingEvent.minute,
                seconds: existingEvent.seconds,
                shot: existingEvent.shot,
                card: existingEvent.card,
                exclusion: existingEvent.exclusion,
                turnover: existingEvent.turnover,
                swimoff: existingEvent.swimoff,
                timeout: existingEvent.timeout,
                challenge: existingEvent.challenge,
                goalkeeperChange: existingEvent.goalkeeperChange,
                activeHomeGoalkeeperMatchPlayerId: existingEvent.activeHomeGoalkeeperMatchPlayerId,
                activeAwayGoalkeeperMatchPlayerId: existingEvent.activeAwayGoalkeeperMatchPlayerId,
                refereeMistake: value ?? undefined,
            } as any);
            payload.id = eventId;
            const result = await client.update(eventId, payload);

            if (result) {
                const newEvents = events.map((e) => (e.id === eventId ? result : e));
                this.setState({ events: newEvents });
            }
        } catch (error) {
            if (error instanceof ApiException) {
                errorWithUserMessage(error.response);
            } else {
                errorWithUserMessage('Error updating referee mistake.');
            }
        }
    };

    private deleteEvent = async (event: EventVm) => {
        const match = this.state.match;
        if (!match) return;

        const pendingKey = getClientEventKey(event);
        if (pendingKey && isPendingEvent(event) && event.id < 0) {
            await eventSubmissionQueue.cancelByClientEventKey(pendingKey);
            success('Queued event cancelled.');
            this.clearEvent();
            return;
        }

        const clientEventKey = newClientEventKey();
        this.setState((prev) => ({
            events: prev.events.map((e) =>
                e.id === event.id ? markPending(e, clientEventKey) : e
            ),
        }));

        void eventSubmissionQueue.enqueue({
            matchId: match.id,
            op: 'delete',
            label: getEventCloudLabel(event, match),
            clientEventKey,
            serverEventId: event.id,
            payload: { eventId: event.id },
            onCancel: () => {
                this.setState((prev) => ({
                    events: prev.events.map((e) => {
                        if (getClientEventKey(e) !== clientEventKey) return e;
                        const cleared = { ...e } as EventVm;
                        delete (cleared as any).clientEventKey;
                        delete (cleared as any).isPending;
                        return cleared;
                    }),
                }));
            },
        });
        this.clearEvent();
    };

    private handleEventDeleted = async (result: EventVm) => {
        const { events, match } = this.state;

        if (!match) return;

        const deletedEvent = events.find((e: EventVm): boolean => e.id === result.id);
        const newEvents = events.filter((e: EventVm): boolean => e.id !== result.id);

        if (deletedEvent?.typeId === EventTypeEnum.Shot) {
            const newMatch = refreshMatchGoals(match, result);

            this.initClock();

            this.setState({
                match: newMatch,
            });
        }

        // After GK change deletion, reload events to get cascade-updated data from backend
        if (deletedEvent?.typeId === EventTypeEnum.Goalkeeper_Change) {
            const eventClient = new EventsClient();
            const freshEvents = await eventClient.getByMatchId(match.id);
            const { homeKeeper, awayKeeper } = MatchReport.deriveActiveGoalkeepers(
                match,
                freshEvents
            );
            // Refetch must not drop rows still queued in the outbox.
            this.setState((prev) => ({
                events: mergeServerWithLocalPending(freshEvents, prev.events),
                selectedHomeKeeper: homeKeeper,
                selectedAwayKeeper: awayKeeper,
            }));
        } else {
            this.setState({ events: newEvents });
        }

        success('Event successfully deleted.');
        this.clearEvent();
    };

    // Runs after outbox flush HTTP success (or outbox published subscription).
    // `isUpdate` / clientEventKey captured at enqueue time.
    private handleEventSaved = async (
        result: EventVm,
        isUpdate: boolean,
        clientEventKey?: string
    ) => {
        const { match } = this.state;

        if (!match) return;

        this.setState((prev) => {
            if (!prev.match) return null;
            const newEvents = clientEventKey
                ? replacePendingWithServer(prev.events, clientEventKey, result)
                : upsertServerEvent(prev.events, result);
            const newMatch = { ...prev.match, ...result.match } as MatchDetailVm;

            const { homeKeeper, awayKeeper } = MatchReport.deriveActiveGoalkeepers(
                newMatch,
                newEvents
            );

            return {
                events: newEvents,
                match: newMatch,
                selectedHomeKeeper: homeKeeper,
                selectedAwayKeeper: awayKeeper,
            };
        });

        await this.maybeAppendDisciplinaryTeamYellow(result, isUpdate);

        if (result.typeId === EventTypeEnum.Goalkeeper_Change) {
            const eventClient = new EventsClient();
            const freshEvents = await eventClient.getByMatchId(match.id);
            // Refetch must not drop rows still queued in the outbox.
            this.setState((prev) => ({
                events: mergeServerWithLocalPending(freshEvents, prev.events),
            }));
        }
    };

    private updateMatchStatus = async (matchStatus: MatchStatusEnum) => {
        const { match } = this.state;

        if (!match) return;

        const matchClient = new MatchesClient();

        const newMatch = {
            ...match,
            statusId: matchStatus,
            status: MatchStatusEnum[matchStatus],
        } as MatchDetailVm;

        try {
            await matchClient.update(newMatch.id, newMatch as unknown as UpdateMatchCommand);
            if (!this.isMatchReportMounted) {
                return;
            }
            this.setState({ match: newMatch });
        } catch (error) {
            if (error instanceof ApiException) {
                errorWithUserMessage(error.response);
            } else {
                errorWithUserMessage('Error saving data.');
            }
        }
    };

    private finishMatch = () => {
        const { match } = this.state;

        if (!match) {
            return;
        }

        popupConfirmWithCallback(
            this.updateMatchStatus,
            MatchStatusEnum.Finished,
            'Are you sure you want to finish the match?'
        );
    };

    private renderEvent = (): React.ReactElement | null => {
        const { match, selectedHomeKeeper, selectedAwayKeeper, events, event } = this.state;

        const lineups = match?.players as CreateMatchPlayerCommandOfMatchPlayer[];

        const eventLog = (
            <MatchEventLog
                match={match}
                events={events}
                lineups={lineups}
                coaches={match?.coaches || []}
                editEvent={this.editEvent}
                onRefereeMistakeChange={this.handleRefereeMistakeChange}
            />
        );

        if (!match || !event) {
            return eventLog;
        }

        switch (event?.typeId) {
            case EventTypeEnum.Turnover:
                return (
                    <Turnover
                        isHomeTeam={getTeamId(event) === match?.homeTeamId}
                        event={event as UpdateEventCommand}
                        lineups={lineups}
                        events={events}
                        updateEvent={this.updateEvent}
                        clearEvent={this.clearEvent}
                        deleteEvent={this.deleteEvent}
                    />
                );
            case EventTypeEnum.Card:
                return (
                    <Card
                        isHomeTeam={getTeamId(event) === match?.homeTeamId}
                        match={match ?? undefined}
                        event={event as UpdateEventCommand}
                        coaches={match?.coaches || []}
                        updateEvent={this.updateEvent}
                        clearEvent={this.clearEvent}
                        deleteEvent={this.deleteEvent}
                    />
                );
            case EventTypeEnum.Swim_Off:
                return (
                    <SwimOff
                        isHomeTeam={getTeamId(event) === match?.homeTeamId}
                        event={event as UpdateEventCommand}
                        lineups={lineups}
                        events={events}
                        updateEvent={this.updateEvent}
                        homeTeamId={match?.homeTeamId}
                        clearEvent={this.clearEvent}
                        deleteEvent={this.deleteEvent}
                    />
                );
            case EventTypeEnum.Exclusion:
                if (event?.exclusion?.excludedCoachId) {
                    return (
                        <CoachExclusion
                            isHomeTeam={getTeamId(event) === match?.homeTeamId}
                            event={event as UpdateEventCommand}
                            coaches={match?.coaches || []}
                            updateEvent={this.updateEvent}
                            clearEvent={this.clearEvent}
                            deleteEvent={this.deleteEvent}
                        />
                    );
                }
                return (
                    <Exclusion
                        isHomeTeam={getTeamId(event) === match?.homeTeamId}
                        event={event as UpdateEventCommand}
                        lineups={lineups}
                        events={events}
                        updateEvent={this.updateEvent}
                        clearEvent={this.clearEvent}
                        deleteEvent={this.deleteEvent}
                    />
                );
            case EventTypeEnum.Shot:
                return (
                    <Shot
                        isHomeTeam={getTeamId(event) === match?.homeTeamId}
                        event={event as UpdateEventCommand}
                        lineups={lineups}
                        events={events}
                        selectedHomeKeeper={selectedHomeKeeper}
                        selectedAwayKeeper={selectedAwayKeeper}
                        penaltyShootoutRecordingMode={
                            this.state.overtimeUiMode === 'penalty_shootout'
                        }
                        updateEvent={this.updateEvent}
                        clearEvent={this.clearEvent}
                        deleteEvent={this.deleteEvent}
                    />
                );
            case 707: // EventTypeEnum.Goalkeeper_Change
            case EventTypeEnum.Goalkeeper_Change:
                return (
                    <GoalkeeperChangeForm
                        event={event as UpdateEventCommand}
                        lineups={lineups}
                        events={events}
                        homeTeamId={match?.homeTeamId}
                        updateEvent={this.updateEvent}
                        clearEvent={this.clearEvent}
                        deleteEvent={this.deleteEvent}
                    />
                );
            case EventTypeEnum.Challenge:
                return (
                    <Challenge
                        isHomeTeam={getTeamId(event) === match?.homeTeamId}
                        event={event as UpdateEventCommand}
                        updateEvent={this.updateEvent}
                        clearEvent={this.clearEvent}
                        deleteEvent={this.deleteEvent}
                    />
                );
            case EventTypeEnum.Timeout:
                return (
                    <Timeout
                        event={event as UpdateEventCommand}
                        match={match ?? undefined}
                        updateEvent={this.updateEvent}
                        clearEvent={this.clearEvent}
                        deleteEvent={this.deleteEvent}
                    />
                );
            default:
                return eventLog;
        }
    };

    public render = (): React.ReactElement => {
        const {
            selectedHomeKeeper,
            selectedAwayKeeper,
            isLoading,
            match,
            event,
            clockState,
            currentPeriod,
            currentTime,
            eventPeriod,
            eventTime,
            isEventValid,
            events,
            showGkSelectionModal,
            isConfirmingStartingGk,
            pendingHomeGk,
            pendingAwayGk,
            overtimeUiMode,
            showPreSwimOffGkModal,
            deferredSwimOffPeriodId,
        } = this.state;
        const { history } = this.props;

        const lineups = (match?.players as CreateMatchPlayerCommandOfMatchPlayer[]) || [];
        const homeLineups = lineups.filter((l) => l.teamId === match?.homeTeamId);
        const awayLineups = lineups.filter((l) => l.teamId === match?.awayTeamId);

        // Time-aware GK highlight: show who is in goal at the current event time
        const timeAwareGks = match
            ? MatchReport.deriveActiveGoalkeepers(match, events, eventPeriod, eventTime)
            : { homeKeeper: selectedHomeKeeper, awayKeeper: selectedAwayKeeper };

        const homeChallengeFailed = this.hasFailedChallengeForTeam(match?.homeTeamId);
        const awayChallengeFailed = this.hasFailedChallengeForTeam(match?.awayTeamId);
        const homeChallengeCount = events.filter(
            (e) => e.typeId === EventTypeEnum.Challenge && e.challenge?.teamId === match?.homeTeamId
        ).length;
        const awayChallengeCount = events.filter(
            (e) => e.typeId === EventTypeEnum.Challenge && e.challenge?.teamId === match?.awayTeamId
        ).length;

        const homeGkLineupForPreSwim = lineups.find(
            (l) => l.teamId === match?.homeTeamId && l.number === selectedHomeKeeper
        );
        const awayGkLineupForPreSwim = lineups.find(
            (l) => l.teamId === match?.awayTeamId && l.number === selectedAwayKeeper
        );
        const homeCapDisplayPreSwim = homeGkLineupForPreSwim
            ? homeGkLineupForPreSwim.capNumber || homeGkLineupForPreSwim.number
            : selectedHomeKeeper;
        const awayCapDisplayPreSwim = awayGkLineupForPreSwim
            ? awayGkLineupForPreSwim.capNumber || awayGkLineupForPreSwim.number
            : selectedAwayKeeper;

        return (
            <Spin tip="Loading..." spinning={isLoading} className="spinner">
                <Content className="o-wrapper">
                    <div className="o-left-screen">
                        <div className="o-left-screen__inner">
                            <div className="o-team o-team--left">
                                <MatchLineup
                                    match={match}
                                    events={
                                        events.filter(
                                            (e: EventVm): boolean =>
                                                getTeamId(e) === match?.homeTeamId
                                        ) || []
                                    }
                                    isHomeTeam
                                    lineup={lineups.filter(
                                        (l: CreateMatchPlayerCommandOfMatchPlayer): boolean =>
                                            l.teamId === match?.homeTeamId
                                    )}
                                    coaches={
                                        match?.coaches?.filter(
                                            (c: MatchCoachVm): boolean =>
                                                c.teamId === match.homeTeamId
                                        ) || []
                                    }
                                    initEvent={this.initEvent}
                                    onPlayerSelect={this.handleKeeperSelect}
                                    selectedKeeper={timeAwareGks.homeKeeper}
                                    activeEvent={event || undefined}
                                />
                            </div>
                            <div className="o-center-buttons" />
                            <div className="o-team o-team--right">
                                <MatchLineup
                                    match={match}
                                    events={
                                        events.filter(
                                            (e: EventVm): boolean =>
                                                getTeamId(e) === match?.awayTeamId
                                        ) || []
                                    }
                                    isHomeTeam={false}
                                    lineup={lineups.filter(
                                        (l: CreateMatchPlayerCommandOfMatchPlayer): boolean =>
                                            l.teamId === match?.awayTeamId
                                    )}
                                    coaches={
                                        match?.coaches?.filter(
                                            (c: MatchCoachVm): boolean =>
                                                c.teamId === match.awayTeamId
                                        ) || []
                                    }
                                    initEvent={this.initEvent}
                                    onPlayerSelect={this.handleKeeperSelect}
                                    selectedKeeper={timeAwareGks.awayKeeper}
                                    activeEvent={event || undefined}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="o-right-screen">
                        <div className="o-right-screen__inner">
                            <header className="o-teams">
                                <div className="c-teams__team c-teams__team--home">
                                    <p>
                                        {match?.homeTeam
                                            ? match?.homeTeam.nameUniversal || match?.homeTeam.name
                                            : ''}
                                    </p>
                                </div>
                                <div className="c-teams__actions">
                                    <span
                                        onClick={() =>
                                            this.initEvent(EventTypeEnum.Timeout, {
                                                teamId: match?.homeTeamId,
                                            })
                                        }
                                        className="c-teams__to"
                                        role="button"
                                    >
                                        {`Timeout: ${
                                            events.filter(
                                                (e) =>
                                                    e.typeId === EventTypeEnum.Timeout &&
                                                    e.timeout?.teamId === match?.homeTeamId
                                            ).length
                                        }`}
                                    </span>
                                    <span
                                        onClick={
                                            !homeChallengeFailed
                                                ? (): void =>
                                                      this.initEvent(EventTypeEnum.Challenge, {
                                                          teamId: match?.homeTeamId,
                                                      })
                                                : undefined
                                        }
                                        className={
                                            homeChallengeFailed
                                                ? 'c-teams__to c-teams__to--challenge c-teams__to--challenge-failed'
                                                : 'c-teams__to c-teams__to--challenge'
                                        }
                                        role="button"
                                    >
                                        {`Challenge: ${homeChallengeCount}`}
                                    </span>
                                </div>
                                {match && (
                                    <div className="c-teams__score-block">
                                        <span className="c-teams__result">{`${
                                            match.homeTeamGoalsTotal !== null
                                                ? match.homeTeamGoalsTotal
                                                : 0
                                        } - ${
                                            match.awayTeamGoalsTotal !== null
                                                ? match.awayTeamGoalsTotal
                                                : 0
                                        }`}</span>
                                        <div
                                            className={
                                                clockState === ClockState.Stopped
                                                    ? 'c-teams__bg-clock c-teams__bg-clock--stopped'
                                                    : 'c-teams__bg-clock c-teams__bg-clock--running'
                                            }
                                            aria-live="polite"
                                        >
                                            {`${shortPeriodClockLabel(
                                                currentPeriod,
                                                overtimeUiMode
                                            )} · ${formatMatchSecondsAsClock(currentTime)}`}
                                        </div>
                                    </div>
                                )}
                                <div className="c-teams__actions">
                                    <span
                                        onClick={() =>
                                            this.initEvent(EventTypeEnum.Timeout, {
                                                teamId: match?.awayTeamId,
                                            })
                                        }
                                        className="c-teams__to"
                                        role="button"
                                    >
                                        {`Timeout: ${
                                            events.filter(
                                                (e) =>
                                                    e.typeId === EventTypeEnum.Timeout &&
                                                    e.timeout?.teamId === match?.awayTeamId
                                            ).length
                                        }`}
                                    </span>
                                    <span
                                        onClick={
                                            !awayChallengeFailed
                                                ? (): void =>
                                                      this.initEvent(EventTypeEnum.Challenge, {
                                                          teamId: match?.awayTeamId,
                                                      })
                                                : undefined
                                        }
                                        className={
                                            awayChallengeFailed
                                                ? 'c-teams__to c-teams__to--challenge c-teams__to--challenge-failed'
                                                : 'c-teams__to c-teams__to--challenge'
                                        }
                                        role="button"
                                    >
                                        {`Challenge: ${awayChallengeCount}`}
                                    </span>
                                </div>
                                <div className="c-teams__team c-teams__team--away">
                                    <p>
                                        {match?.awayTeam
                                            ? match?.awayTeam.nameUniversal || match?.awayTeam.name
                                            : ''}
                                    </p>
                                </div>
                            </header>
                            <MatchClock
                                event={event}
                                clockState={clockState}
                                eventPeriod={eventPeriod}
                                eventTime={eventTime}
                                overtimeUiMode={overtimeUiMode}
                                isEventValid={isEventValid}
                                startClock={this.startClock}
                                pauseClock={this.pauseClock}
                                stopClock={this.stopClock}
                                resetClock={this.resetClock}
                                setPeriod={this.setPeriod}
                                modifyClock={this.modifyClock}
                                saveEvent={this.saveEvent}
                            />
                            {deferredSwimOffPeriodId !== null &&
                                !events.some(
                                    (e: EventVm): boolean =>
                                        e.typeId === EventTypeEnum.Swim_Off &&
                                        e.periodId === deferredSwimOffPeriodId
                                ) && (
                                    <Alert
                                        type="info"
                                        showIcon
                                        message="Swim-off pending"
                                        description="Record one or both goalkeeper changes if needed, then continue."
                                        action={
                                            <Button
                                                type="primary"
                                                size="small"
                                                disabled={!!event}
                                                onClick={() => this.maybeStartDeferredSwimOff()}
                                            >
                                                Continue to swim-off
                                            </Button>
                                        }
                                    />
                                )}
                            {this.renderEvent()}
                        </div>
                    </div>
                    <div className="c-extended-menu">
                        <button
                            type="button"
                            className="c-btn c-btn--side c-btn--right c-btn--extended-menu-btn c-extended-menu__btn u-flex u-align-items-center u-justify-content-center"
                        >
                            <Icon name="icon-chevron-left" />
                        </button>
                        <div className="c-extended-menu__list u-flex u-flex-row u-flex-nowrap">
                            <div
                                onClick={() => history.push(`/`)}
                                className="c-extended-menu__list u-flex u-flex-row u-flex-nowrap"
                                role="button"
                            >
                                <div className="c-extended-menu__item u-flex u-flex-column u-align-items-center u-justify-content-center">
                                    <Icon name="icon-swimmer" />
                                    <span>Back to App</span>
                                </div>
                            </div>
                            <div
                                onClick={() => history.push(`/match-log/${match?.id}`)}
                                className="c-extended-menu__list u-flex u-flex-row u-flex-nowrap"
                                role="button"
                            >
                                <div className="c-extended-menu__item u-flex u-flex-column u-align-items-center u-justify-content-center">
                                    <Icon name="icon-clipboard-list" />
                                    <span>Match Log</span>
                                </div>
                            </div>
                            {match?.statusId !== MatchStatusEnum.Finished && (
                                <div
                                    onClick={() => this.finishMatch()}
                                    className="c-extended-menu__list u-flex u-flex-row u-flex-nowrap"
                                    role="button"
                                >
                                    <div className="c-extended-menu__item u-flex u-flex-column u-align-items-center u-justify-content-center">
                                        <Icon name="icon-whistle-solid" />
                                        <span>Finish Match</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Content>
                {match ? (
                    <div className="offline-status-dock">
                        <OfflineStatusControl matchId={match.id} variant="desktop" />
                    </div>
                ) : null}
                <Modal
                    title="Active goalkeepers"
                    visible={showPreSwimOffGkModal}
                    closable={false}
                    maskClosable={false}
                    footer={[
                        <Button
                            key="no-changes"
                            onClick={() => {
                                const period = this.state.preSwimOffPeriod;
                                this.setState(
                                    {
                                        showPreSwimOffGkModal: false,
                                        preSwimOffPeriod: null,
                                    },
                                    () => {
                                        if (period != null) {
                                            this.initEvent(
                                                EventTypeEnum.Swim_Off,
                                                undefined,
                                                period
                                            );
                                        }
                                    }
                                );
                            }}
                        >
                            No changes
                        </Button>,
                        <Button
                            key="gk-first"
                            type="primary"
                            onClick={() => {
                                const period = this.state.preSwimOffPeriod;
                                if (period == null) {
                                    return;
                                }
                                this.setState({
                                    showPreSwimOffGkModal: false,
                                    preSwimOffPeriod: null,
                                    deferredSwimOffPeriodId: period,
                                });
                            }}
                        >
                            Record goalkeeper changes first
                        </Button>,
                    ]}
                >
                    <p>
                        {`White #${homeCapDisplayPreSwim} and blue #${awayCapDisplayPreSwim} are active goalkeepers. 
                        You can record goalkeeper changes first or start the swim-off immediately with same goalkeepers.
                        Double-click a cap number to change.`}
                    </p>
                </Modal>
                <Modal
                    title="Select starting goalkeepers"
                    visible={showGkSelectionModal}
                    closable={false}
                    okText="Confirm & Start"
                    confirmLoading={isConfirmingStartingGk}
                    cancelButtonProps={{ style: { display: 'none' } }}
                    okButtonProps={{
                        disabled: !pendingHomeGk || !pendingAwayGk || isConfirmingStartingGk,
                    }}
                    onOk={async () => {
                        if (!pendingHomeGk || !pendingAwayGk || isConfirmingStartingGk) {
                            return;
                        }
                        this.setState({ isConfirmingStartingGk: true });
                        try {
                            const created = await this.createStartingGoalkeeperEvents(
                                pendingHomeGk,
                                pendingAwayGk
                            );
                            if (created) {
                                this.setState(
                                    {
                                        showGkSelectionModal: false,
                                        isConfirmingStartingGk: false,
                                        pendingHomeGk: null,
                                        pendingAwayGk: null,
                                    },
                                    () => {
                                        this.initEvent(EventTypeEnum.Swim_Off);
                                    }
                                );
                            } else {
                                this.setState({ isConfirmingStartingGk: false });
                            }
                        } catch {
                            this.setState({ isConfirmingStartingGk: false });
                        }
                    }}
                >
                    <div style={{ marginBottom: 16 }}>
                        <strong>White team — select goalkeeper:</strong>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                            {[...homeLineups]
                                .sort(
                                    (a, b) => (a.capNumber || a.number) - (b.capNumber || b.number)
                                )
                                .map((l) => (
                                    <Button
                                        key={l.number}
                                        type={pendingHomeGk === l.number ? 'primary' : 'default'}
                                        danger={pendingHomeGk === l.number}
                                        disabled={isConfirmingStartingGk}
                                        onClick={() => this.setState({ pendingHomeGk: l.number })}
                                        style={{ minWidth: 40 }}
                                    >
                                        {l.capNumber || l.number}
                                    </Button>
                                ))}
                        </div>
                    </div>
                    <div>
                        <strong>Blue team — select goalkeeper:</strong>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                            {[...awayLineups]
                                .sort(
                                    (a, b) => (a.capNumber || a.number) - (b.capNumber || b.number)
                                )
                                .map((l) => (
                                    <Button
                                        key={l.number}
                                        type={pendingAwayGk === l.number ? 'primary' : 'default'}
                                        danger={pendingAwayGk === l.number}
                                        disabled={isConfirmingStartingGk}
                                        onClick={() => this.setState({ pendingAwayGk: l.number })}
                                        style={{ minWidth: 40 }}
                                    >
                                        {l.capNumber || l.number}
                                    </Button>
                                ))}
                        </div>
                    </div>
                </Modal>
            </Spin>
        );
    };
}

export default MatchReport;
